import {Application} from "@hotwired/stimulus"
import StimulusControllerResolver from "stimulus-controller-resolver"
import HelpscoutController from "./controllers/helpscout_controller"
import FadingBannerController from "./controllers/fading_banner_controller"
import DelaySubmitController from "./controllers/delay_submit_controller"
import ShopifyBuyButtonController from
  "./controllers/shopify_buy_button_controller"
import BounceModalController from "./controllers/bounce_modal_controller"
import SearchFieldController from "./controllers/search_field_controller"
import ApplicationController from "./controllers/application_controller"
import ShowButtonController from "./controllers/show_button_controller"
import SlideoutController from "./controllers/slideout_controller"
import HeadroomController from "./controllers/headroom_controller"
import SidebarController from "./controllers/sidebar_controller"
import OfflineController from "./controllers/offline_controller"
import HeadwayController from "./controllers/headway_controller"
import TooltipController from "./controllers/tooltip_controller"
import FlashController from "./controllers/flash_controller"
import FactsTableController from "./controllers/facts_table_controller"
import LaddaController from "./controllers/ladda_controller"
import TabsController from "./controllers/tabs_controller"
import InfiniteScrollingController from
  "./controllers/infinite_scrolling_controller"
import ToggleVisibilityController from
  "./controllers/toggle_visibility_controller"
import ClampVisibilityController from
    "./controllers/clamp_visibility_controller"
import LanguageSelectionController from
  "./controllers/language_selection_controller"
import DashboardDueQuizzesController
  from "./controllers/dashboard_due_quizzes_controller"
import PopoverController from "./controllers/popover_controller"
import TimeoutController from "./controllers/timeout_controller"

function stimulus_loader() {
  const stimulus = Application.start()
  stimulus.registerActionOption("Y150", ({event}) => (
    event.type === "scroll" ? window.scrollY > 150 : true
  ))
  stimulus.register("tabs", TabsController)
  stimulus.register("flash", FlashController)
  stimulus.register("facts_table", FactsTableController)
  stimulus.register("ladda", LaddaController)
  stimulus.register("tooltip", TooltipController)
  stimulus.register("headway", HeadwayController)
  stimulus.register("timeout", TimeoutController)
  stimulus.register("offline", OfflineController)
  stimulus.register("sidebar", SidebarController)
  stimulus.register("slideout", SlideoutController)
  stimulus.register("headroom", HeadroomController)
  stimulus.register("show_button", ShowButtonController)
  stimulus.register("application", ApplicationController)
  stimulus.register("search_field", SearchFieldController)
  stimulus.register("bounce_modal", BounceModalController)
  stimulus.register("fading_banner", FadingBannerController)
  stimulus.register("delay_submit", DelaySubmitController)
  stimulus.register("toggle_visibility", ToggleVisibilityController)
  stimulus.register("clamp_visibility", ClampVisibilityController)
  stimulus.register("language_selection", LanguageSelectionController)
  stimulus.register("infinite_scrolling", InfiniteScrollingController)
  stimulus.register("shopify_buy_button", ShopifyBuyButtonController)
  stimulus.register("dashboard_due_quizzes", DashboardDueQuizzesController)
  stimulus.register("helpscout", HelpscoutController)
  stimulus.register("popover", PopoverController)
  register_controllers_dynamically(stimulus)
}

const register_controllers_dynamically = (stimulus) => {
  // the stimulus controller resolver calls the callback below for any found
  // controller data tags which aren't registered yet.
  // https://github.com/danieldiekmeier/stimulus-controller-resolver
  StimulusControllerResolver.install(stimulus, async name => {
    if (!name) return

    if (!routes[name]) {
      throw new Error(`Can't find dynamic controller for ${name}`)
    }

    try {
      // dynamically load the controller module
      const module = await routes[name]()
      return module.default
    } catch (error) {
      get_offline_controller(stimulus).show_offline_message()
      // eslint-disable-next-line no-console
      console.error(error)
    }
  })
}

const get_offline_controller = (stimulus) =>
  stimulus.getControllerForElementAndIdentifier(
    document.getElementById("offline_messages"),
    "offline"
  )

const routes = {
  home: () =>
    import(
      /* webpackChunkName: "home_controller" */
      "./controllers/home_controller"
    ),
  study_unit: () =>
    import(
      /* webpackChunkName: "study_unit_controller" */
      "./controllers/study_unit_controller"
    ),
  study_unit_listing_item: () =>
    import(
      /* webpackChunkName: "study_unit_listing_item_controller" */
      "./controllers/study_unit_listing_item_controller"
    ),
  article: () =>
    import(
      /* webpackChunkName: "article_controller" */
      "./controllers/article_controller"
    ),
  atlas: () =>
    import(
      /* webpackChunkName: "atlas_controller" */
      "./controllers/atlas_controller"
    ),
  video: () =>
    import(
      /* webpackChunkName: "video_controller" */
      "./controllers/video_controller"
    ),
  confirm_code: () =>
    import(
      /* webpackChunkName: "confirm_code_controller" */
      "./controllers/confirm_code_controller.js"
    ),
  login: () =>
    import(
      /* webpackChunkName: "login_controller" */
      "./controllers/login_controller"
    ),
  registration: () =>
    import(
      /* webpackChunkName: "registration_controller" */
      "./controllers/registration_controller"
    ),
  custom_quiz_creator: () =>
    import(
      /* webpackChunkName: "custom_quiz_creator_controller" */
      "./controllers/custom_quiz_creator_controller"
    ),
  custom_quiz_summary: () =>
    import(
      /* webpackChunkName: "custom_quiz_summary_controller" */
      "./controllers/custom_quiz_summary_controller"
    ),
  saved_custom_quiz: () =>
    import(
      /* webpackChunkName: "saved_custom_quiz_controller" */
      "./controllers/saved_custom_quiz_controller"
    ),
  due_quiz: () =>
    import(
      /* webpackChunkName: "due_quiz_controller" */
      "./controllers/due_quiz_controller"
    ),
  profile_setup: () =>
    import(
      /* webpackChunkName: "profile_setup_controller" */
      "./controllers/profile_setup_controller.js"
    ),
  profile: () =>
    import(
      /* webpackChunkName: "profile_controller" */
      "./controllers/profile_controller.js"
    ),
  trainer: () =>
    import(
      /* webpackChunkName: "trainer_controller" */
      "./controllers/trainer_controller.js"
    ),
  search: () =>
    import(
      /* webpackChunkName: "search_controller" */
      "./controllers/search_controller"
    ),
  content_filter: () =>
    import(
      /* webpackChunkName: "content_filter_controller" */
      "./controllers/content_filter_controller"
    ),
  dashboard: () =>
    import(
      /* webpackChunkName: "dashboard_controller" */
      "./controllers/dashboard_controller"
    ),
  pricing: () =>
    import(
      /* webpackChunkName: "pricing_controller" */
      "./controllers/pricing_controller"
    ),
  checkout: () =>
    import(
      /* webpackChunkName: "checkout_controller" */
      "./controllers/checkout_controller"
    ),
  payment_status: () =>
    import(
      /* webpackChunkName: "payment_status_controller" */
      "./controllers/payment_status_controller"
    ),
  subscription: () =>
    import(
      /* webpackChunkName: "subscription_controller" */
      "./controllers/subscription_controller"
    ),
  carousel: () =>
    import(
      /* webpackChunkName: "carousel_controller" */
      "./controllers/carousel_controller"
    ),
  interactive_quiz_page: () =>
    import(
      /* webpackChunkName: "interactive_quiz_page_controller" */
      "./controllers/interactive_quiz_page_controller"
    )
}
export default stimulus_loader
